import { useState } from "react";
import { X, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { useCategories } from "../../lib/useCategories";

const OTHER_ID = "Lainnya";
const OTHER_EN = "Other";

export default function LeadForm({ open, onClose }) {
  const { lang } = useLang();
  const { categories } = useCategories();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
    website: "", // honeypot
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const typeOptions = [
    ...categories.map((c) => (lang === "id" ? (c.name?.id || c.slug) : (c.name?.en || c.slug))),
    lang === "id" ? OTHER_ID : OTHER_EN,
  ];

  const labels = lang === "id"
    ? {
        title: "Mulai konsultasi.",
        sub: "Ceritakan rencana Anda. Kami balas dengan estimasi awal dan langkah berikutnya.",
        name: "Nama lengkap",
        email: "Email",
        phone: "Telepon / WhatsApp",
        type: "Jenis proyek",
        message: "Cerita singkat tentang proyek",
        send: "Kirim",
        sending: "Mengirim…",
        close: "Tutup",
        success: "Terkirim. Tim kami akan menghubungi Anda dalam 1–2 hari kerja.",
        rate: "Terlalu banyak pengiriman. Coba lagi nanti.",
        required: "Harus diisi.",
        invalidEmail: "Format email tidak valid.",
        tooShort: "Pesan terlalu pendek (min. 10 karakter).",
        types: typeOptions,
      }
    : {
        title: "Start a consultation.",
        sub: "Tell us your plan. We'll respond with an early estimate and clear next steps.",
        name: "Full name",
        email: "Email",
        phone: "Phone / WhatsApp",
        type: "Project type",
        message: "Short brief about the project",
        send: "Send",
        sending: "Sending…",
        close: "Close",
        success: "Sent. Our team will be in touch within 1–2 working days.",
        rate: "Too many submissions. Please try again later.",
        required: "Required.",
        invalidEmail: "Invalid email address.",
        tooShort: "Message is too short (min. 10 characters).",
        types: typeOptions,
      };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = labels.required;
    if (!form.email.trim()) e.email = labels.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = labels.invalidEmail;
    if (!form.message.trim()) e.message = labels.required;
    else if (form.message.trim().length < 10) e.message = labels.tooShort;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/leads", form);
      // Honeypot tripped server-side → silently behave as success.
      if (res.data?.status === "filtered" || res.data?.id) {
        setSuccess(true);
        setForm({ name: "", email: "", phone: "", project_type: "", message: "", website: "" });
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 429) setError(labels.rate);
      else setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-form-title"
      className="fixed inset-0 z-[60] bg-[#1b1c1e]/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lead-form-modal"
    >
      <div
        className="bg-white max-w-[640px] w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-8 py-5 border-b border-[#e3e3e1]">
          <div
            id="lead-form-title"
            className="font-display font-semibold text-[#16181c] text-xl tracking-[-0.01em]"
          >
            {labels.title}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={labels.close}
            className="p-2 -mr-2 text-[#5d5f64] hover:text-[#16181c]"
            data-testid="lead-form-close"
          >
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className="px-8 py-12 text-center" data-testid="lead-form-success">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[#16181c] mb-5">
              <Check size={20} />
            </div>
            <p className="text-[#16181c] text-base max-w-[40ch] mx-auto">{labels.success}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-8 inline-flex items-center text-[#16181c] border-b border-[#16181c] pb-1 text-sm"
            >
              {labels.close}
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-8 py-6 space-y-5" data-testid="lead-form" noValidate>
            <p className="text-sm text-[#5d5f64] leading-relaxed">{labels.sub}</p>

            {/* Honeypot — hidden via CSS, real users won't see/fill it */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <label>
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LF label={labels.name} error={errors.name}>
                <input
                  required
                  className="lf-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="lead-form-name"
                  autoComplete="name"
                />
              </LF>
              <LF label={labels.email} error={errors.email}>
                <input
                  required
                  type="email"
                  className="lf-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="lead-form-email"
                  autoComplete="email"
                />
              </LF>
              <LF label={labels.phone}>
                <input
                  className="lf-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="lead-form-phone"
                  autoComplete="tel"
                />
              </LF>
              <LF label={labels.type}>
                <select
                  className="lf-input"
                  value={form.project_type}
                  onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                  data-testid="lead-form-type"
                >
                  <option value="">—</option>
                  {labels.types.map((tt) => (
                    <option key={tt} value={tt}>
                      {tt}
                    </option>
                  ))}
                </select>
              </LF>
            </div>
            <LF label={labels.message} error={errors.message}>
              <textarea
                required
                rows={5}
                className="lf-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="lead-form-message"
              />
            </LF>
            {error && (
              <div
                role="alert"
                className="text-sm text-[#16181c] bg-[#f3f3f0] border border-[#e3e3e1] px-4 py-3"
                data-testid="lead-form-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#16181c] text-white px-6 py-3 font-medium hover:bg-[#5d5f64] disabled:opacity-60"
              data-testid="lead-form-submit"
            >
              {submitting ? labels.sending : labels.send}
            </button>
          </form>
        )}

        <style>{`
          .lf-input {
            width: 100%;
            background: #ffffff;
            border: 1px solid #e3e3e1;
            padding: 10px 12px;
            font-family: "Inter", sans-serif;
            font-size: 14px;
            color: #16181c;
          }
          .lf-input:focus { outline: none; border-color: #16181c; }
        `}</style>
      </div>
    </div>
  );
}

function LF({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#5d5f64] mb-2">{label}</span>
      {children}
      {error && (
        <span className="block mt-1 text-xs text-[#16181c]" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}
